// Components==============
import { useRedux } from 'hooks/useRedux';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { useContext } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { EditableProcessEntity } from 'hooks/useProcessGroups';
import AddSwitchButton from './components/AddSwitchModalButton/AddSwitchModalButton';
import Process from './components/Process/Process';
import EndPoint from './components/EndPoint/EndPoint';
import StartPoint from './components/StartPoint/StartPoint';
import { ProjectProcessContext } from './ProjectProcess';
import './ProjectProcess.module.scss';
// =========================

export const Start = () => {
  const { processGroups, isEditable } = useContext(ProjectProcessContext);

  const group = processGroups?.find((g) => g.activities[0]?.id === 6);

  const token = useRedux((state) =>
    group?.id ? state.phase?.entities.byId[group?.id] : undefined
  );

  return (
    <div styleName={`group ${group?.phase}`}>
      <StartPoint token={token} process={group} />
      {isEditable && (
        <div styleName="first-add">
          <AddSwitchButton permission={'App\\Phase'} fn="add" />
        </div>
      )}
    </div>
  );
};

export const End = () => {
  const { processGroups } = useContext(ProjectProcessContext);

  const group = processGroups?.find((g) => g.activities[0]?.id === 7);

  const token = useRedux((state) =>
    group?.id ? state.phase?.entities.byId[group?.id] : undefined
  );

  return (
    <div styleName={`group ${group?.phase}`}>
      <EndPoint token={token} process={group} />
    </div>
  );
};

export const ProcessSteps = () => {
  const { processGroups, isEditable } = useContext(ProjectProcessContext);

  const groups = processGroups?.filter(
    (g) => g.activities[0]?.id !== 7 && g.activities[0]?.id !== 6
  );

  const { handleReorder } = useContext(ProjectDetailsContext);

  const activities = useRedux((state) => {
    const data = state.activity?.entities.allIds
      .map((id) => state.activity?.entities.byId[id])
      .filter((a) => a?.type === 'regular')
      // filter vrije activiteiten duplicates
      .filter(
        (value, i, arr) =>
          arr.findIndex((activity) => activity?.name === value?.name) === i
      );

    return data;
  });

  // OnbekendToken option disabled for now in ProjectController
  // If unknown token was too unreliable to automatically select (in ProjectController), it shows the thumbnail of
  // highestReliability while still being an Unknown token with all info of possible tokens in description
  const getOnbekendToken = (act: EditableProcessEntity) => {
    const description = JSON.parse(act.description);
    let highestReliability = description[0];
    let oldReliability = 0;
    if (description.length > 1) {
      description.forEach((d) => {
        if (d.reliability > oldReliability) {
          oldReliability = d.reliability;
          highestReliability = d;
        }
      });
    }

    const id = activities?.find(
      (activity) => activity?.slug === highestReliability.slug
    )?.id;

    const newActivity: EditableProcessEntity = {
      description: act.description,
      order: act.order,
      id: id || 61,
      process_type: act.process_type,
      processId: act.processId || undefined,
      finished: act.finished,
      users: act.users,
      date: act.date,
      date_type: act.date_type,
    };
    return newActivity;
  };

  return (
    <DragDropContext onDragEnd={handleReorder}>
      <Droppable
        droppableId="phases"
        direction="horizontal"
        isDropDisabled={!isEditable}
      >
        {(groupProvided) => (
          <div
            {...groupProvided.droppableProps}
            ref={groupProvided.innerRef}
            style={{
              display: 'grid',
              gap: '2em',
              gridAutoFlow: 'column',
            }}
          >
            {groups?.map((group, index) => (
              <Draggable
                draggableId={`${group.phase}-${index}`}
                key={`${group.phase}-${index}`}
                index={index}
                isDragDisabled={!isEditable}
              >
                {(groupDragProvided) => (
                  <div
                    ref={groupDragProvided.innerRef}
                    {...groupDragProvided.draggableProps}
                  >
                    <DragDropContext onDragEnd={handleReorder}>
                      <Droppable
                        droppableId={`${index + 1}`}
                        direction="horizontal"
                        isDropDisabled={!isEditable}
                      >
                        {(provided) => (
                          <div
                            styleName={`group ${group.phase}`}
                            key={index}
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {group.activities.map((activity, i) =>
                              // If not onbekend token
                              activity.id !==
                              activities?.find((a) => a?.slug === 'onbekend')
                                ?.id ? (
                                  <Process
                                  dragHandleProps={
                                    i === 0
                                      ? groupDragProvided.dragHandleProps
                                      : undefined
                                  }
                                  editableProcess={activity}
                                  phase={group.id}
                                  phaseIndex={index + 1}
                                  processIndex={i + 1}
                                  lastOfGroup={
                                    groups[index].activities.length === i + 1
                                  }
                                  index={i}
                                />
                              ) : (
                                // use the stand in onbekend token
                                <Process
                                  dragHandleProps={
                                    i === 0
                                      ? groupDragProvided.dragHandleProps
                                      : undefined
                                  }
                                  editableProcess={getOnbekendToken(activity)}
                                  phase={group.id}
                                  phaseIndex={index + 1}
                                  processIndex={i + 1}
                                  lastOfGroup={
                                    groups[index].activities.length === i + 1
                                  }
                                  index={i}
                                  onbekend
                                />
                              )
                            )}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                )}
              </Draggable>
            ))}
            {groupProvided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
